/**
 * 브랜드 Instagram 계정 ↔ Unipile 연동 매핑 키
 * Unipile hosted auth `name` 파라미터 및 Firestore `unipile.connectionKey` 에 사용
 */
const UNIPILE_CONNECTION_KEY_PREFIX = 'cm';

/** 소비처마다 자기 계정 타입이 있다 — 여기선 읽는 필드만 선택으로 받는다(구조적 호환) */
type BrandAccountLike = {
  platform?: unknown
  instagramUsername?: unknown
  unipile?: unknown
}

export function buildBrandAccountConnectionKey(workspaceId: unknown, username: unknown) {
  const wsId = String(workspaceId || '').trim();
  const uname = String(username || '').trim().toLowerCase();
  if (!wsId || !uname) return '';
  return `${UNIPILE_CONNECTION_KEY_PREFIX}:${wsId}:instagram:${uname}`;
}

/**
 * @param {string} name - Unipile webhook/redirect `name` 값
 * @returns {{ workspaceId: string, platform: string, username: string, connectionKey: string } | null}
 */
export function parseBrandAccountConnectionKey(name: unknown) {
  const raw = String(name || '').trim();
  if (!raw.startsWith(`${UNIPILE_CONNECTION_KEY_PREFIX}:`)) return null;

  const parts = raw.split(':');
  if (parts.length < 4 || parts[0] !== UNIPILE_CONNECTION_KEY_PREFIX || parts[2] !== 'instagram') {
    return null;
  }

  const workspaceId = parts[1];
  const username = parts.slice(3).join(':').trim().toLowerCase();
  if (!workspaceId || !username) return null;

  return {
    workspaceId,
    platform: 'instagram',
    username,
    connectionKey: raw,
  };
}

export function getInstagramUsernameFromBrandAccount(account: BrandAccountLike | null | undefined) {
  return String(account?.instagramUsername || '').trim().replace(/^@+/, '');
}

export function getUnipileFromBrandAccount(account: BrandAccountLike | null | undefined) {
  const unipile = (account?.unipile && typeof account.unipile === 'object' ? account.unipile : {}) as { accountId?: unknown; connectionKey?: unknown; status?: unknown; provider?: unknown; connectedAt?: unknown };
  return {
    accountId: String(unipile.accountId || '').trim(),
    connectionKey: String(unipile.connectionKey || '').trim(),
    status: String(unipile.status || '').trim(),
    provider: String(unipile.provider || 'INSTAGRAM').trim(),
    connectedAt: String(unipile.connectedAt || '').trim(),
  };
}

export function isBrandAccountUnipileFailed(account: BrandAccountLike | null | undefined) {
  const { status } = getUnipileFromBrandAccount(account);
  return String(status || '').trim().toUpperCase() === 'FAILED';
}

export function isBrandAccountUnipileConnected(account: BrandAccountLike | null | undefined) {
  if (isBrandAccountUnipileFailed(account)) return false;
  const { accountId } = getUnipileFromBrandAccount(account);
  return Boolean(accountId);
}

/** 워크스페이스 brandAccounts 중 Unipile 연동 완료된 Instagram 계정 수 */
export function countConnectedInstagramBrandAccounts(accounts: Array<BrandAccountLike | null | undefined> | null | undefined) {
  return (Array.isArray(accounts) ? accounts : []).filter((account) => {
    const platform = String(account?.platform || '').toLowerCase();
    return platform === 'instagram' && isBrandAccountUnipileConnected(account);
  }).length;
}
